import { createSlice } from "@reduxjs/toolkit";

// Global upload manager. Tracks every in-flight file upload (photo / video /
// doc) so progress survives navigation and a failed upload stays on screen
// until the user dismisses it. State lives at the app root (not in any page
// component), so unmounting the submitting page does not drop the upload UI.
//
// upload shape: { id, label, progress (0-100), status, error }
//   status: "uploading" | "success" | "error"
const initialState = {
  uploads: [],
};

const uploadManagerSlice = createSlice({
  name: "uploadManager",
  initialState,
  reducers: {
    addUpload: (state, action) => {
      const { id, label } = action.payload;
      if (state.uploads.some((u) => u.id === id)) return;
      state.uploads.push({
        id,
        label: label || "Upload",
        progress: 0,
        status: "uploading",
        error: null,
      });
    },
    // Idempotent — also used to flip an errored upload back to in-flight on retry.
    markUploading: (state, action) => {
      const u = state.uploads.find((x) => x.id === action.payload.id);
      if (u) {
        u.status = "uploading";
        u.progress = 0;
        u.error = null;
      }
    },
    setUploadProgress: (state, action) => {
      const { id, progress } = action.payload;
      const u = state.uploads.find((x) => x.id === id);
      if (u && u.status === "uploading") {
        u.progress = Math.max(0, Math.min(100, progress));
      }
    },
    setUploadSuccess: (state, action) => {
      const u = state.uploads.find((x) => x.id === action.payload.id);
      if (u) {
        u.status = "success";
        u.progress = 100;
        u.error = null;
      }
    },
    setUploadError: (state, action) => {
      const { id, error } = action.payload;
      const u = state.uploads.find((x) => x.id === id);
      if (u) {
        u.status = "error";
        u.error = error || "Upload failed";
      }
    },
    removeUpload: (state, action) => {
      state.uploads = state.uploads.filter((u) => u.id !== action.payload);
    },
  },
});

export const {
  addUpload,
  markUploading,
  setUploadProgress,
  setUploadSuccess,
  setUploadError,
  removeUpload,
} = uploadManagerSlice.actions;

export default uploadManagerSlice.reducer;
